import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { ConfirmationDialogModel, ConfirmDialogComponent } from '@y42/core/dialog/confirm-dialog';
import { GitDiff, GitQuery, GitService } from '@y42/git';
import { ToastService } from '@y42/toast';
import { defer, filter, switchMap, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'y42-git-stashed-changes-dialog',
  templateUrl: './git-stashed-changes-dialog.component.html',
  styleUrls: ['./git-stashed-changes-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GitStashedChangesDialogComponent {
  constructor(
    private readonly gitQuery: GitQuery,
    private readonly gitService: GitService,
    private readonly toastService: ToastService,
    private readonly dialogRef: MatDialogRef<GitStashedChangesDialogComponent>,
    private readonly dialog: MatDialog,
  ) {}
  diffs = [];

  activeItem: GitDiff | null = null;

  readonly state = connectState(this, {
    diffs: defer(() => this.gitService.getBranchStashedChanges()).pipe(
      tap((diffs) => {
        this.activeItem = diffs[0] ?? null;
      }),
    ),
    hasChangedFiles: this.gitQuery.hasModifiedFiles$,
  });

  async discardStash() {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '600px',
        panelClass: ['confirmation-pop-up'],
        data: {
          header_text: 'Discard stash?',
          text: `Are you sure you want to discard these stashed changes?`,
          data: true,
          confirmation_text: 'Discard',
        } as ConfirmationDialogModel,
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.clearStash()),
      )
      .subscribe();
  }

  async restore() {
    try {
      const stashes = await this.gitService.getBranchStashes();
      const firstStash = stashes[0];
      if (!firstStash) {
        return;
      }
      await this.gitService.restoreStash(firstStash.index);
      this.toastService.success('Stashed changes were successfully applied');
      await this.clearStash();
    } catch (e) {
      this.toastService.error('There was an error while applying stashed changes');
    }
  }

  private async clearStash() {
    await this.gitService.clearBranchStashes();
    this.dialogRef.close();
  }
}
